import {
  EnumFormGenComponentType,
  type IFormGenSchema,
  type TFormGenUiSchema,
  type TJSONSchema,
} from "@artifact-management/json-dynamic-form";

export const offerJsonSchema = {
  type: "object",
  $defs: {
    isCdi: {
      properties: { contractType: { properties: { value: { const: "cdi" } } } },
    },
    isPermanentOffer: {
      properties: { isPermanent: { const: true } },
    },
    isOfferLocked: {
      properties: { isOfferLocked: { const: true } },
      required: ["isOfferLocked"],
    },
    hasName: {
      properties: { name: { minLength: 1 } },
    },
    // Champ observé par FormGenInputAppellation pour déclencher sa recherche automatique.
    appellationSourceProperty: { const: "name" },
  },
  properties: {
    url: {
      type: "string",
      format: "url",
    },
    name: {
      type: "string",
      minLength: 1,
      errorMessage: { minLength: "Ce champ est obligatoire" },
    },
    contractType: {
      type: "object",
      setting: { domain: "settings", settingType: "contract-types" },
    },
    isPermanent: {
      type: "boolean",
    },
    contractDurationValue: {
      type: "number",
      minimum: 0,
    },
    contractDurationUnit: {
      type: "object",
      enum: [
        { value: "day", label: "Jours" },
        { value: "week", label: "Semaines" },
        { value: "month", label: "Mois" },
        { value: "year", label: "Années" },
      ],
    },
    startDate: {
      type: "string",
      format: "date",
    },
    expirationDate: {
      type: "string",
      format: "date",
    },
    appellation: {
      type: "object",
      appellation: {},
      errorMessage: "Ce champ est obligatoire",
    },
    address: {
      type: "object",
      address: {},
      errorMessage: "Ce champ est obligatoire",
    },
    workWeeklyDuration: {
      type: "number",
      minimum: 0,
      maximum: 100,
    },
    externalSource: {
      type: "object",
      setting: { domain: "settings", settingType: "offer_origin" },
      errorMessage: "Ce champ est obligatoire",
    },
    description: {
      type: "string",
      format: "richtext",
    },
    enterprise: {
      type: "object",
      enterprise: {},
      errorMessage: "Ce champ est obligatoire",
    },
    contact: {
      type: "object",
      contact: { existingContacts: { sourceProperty: "enterprise" }, place: true, comment: true },
    },
    isOfferLocked: {
      type: "boolean",
    },
  },
  additionalProperties: false,
  required: ["name", "address", "contractType", "externalSource", "enterprise", "appellation"],
  allOf: [
    {
      if: { $ref: "#/$defs/isPermanentOffer" },
      else: {
        properties: {
          expirationDate: { minLength: 1, errorMessage: { minLength: "Ce champ est obligatoire" } },
        },
        required: ["expirationDate"],
      },
    },
  ],
} as const satisfies TJSONSchema;

export const offerUiSchema: TFormGenUiSchema<typeof offerJsonSchema>[] = [
  {
    type: EnumFormGenComponentType.INPUT,
    label: "URL de l'offre",
    scope: "url",
    columns: { xs: 12 },
  },
  {
    type: EnumFormGenComponentType.TITLE,
    title: "Détails du poste",
    tooltip:
      "Tapez le poste occupé, une appellation correspondante sera automatiquement détectée. N'hésitez pas à modifier cette suggestion si elle ne vous convient pas.",
    columns: { xs: 12 },
  },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "Poste occupé *",
    scope: "name",
    columns: { xs: 12, sm: 6 },
    rules: [{ rule: "isOfferLocked", override: { disabled: true } }],
  },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "Appellation détectée *",
    scope: "appellation",
    columns: { xs: 12, sm: 6 },
    disabled: true,
    rules: [
      { rule: "hasName", override: { disabled: false } },
      { rule: "isOfferLocked", override: { disabled: true } },
    ],
  },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "Adresse *",
    scope: "address",
    columns: { xs: 12 },
    rules: [{ rule: "isOfferLocked", override: { disabled: true } }],
  },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "Type de contrat *",
    scope: "contractType",
    columns: { xs: 12, sm: 6 },
    rules: [{ rule: "isOfferLocked", override: { disabled: true } }],
  },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "Durée",
    scope: "contractDurationValue",
    columns: { xs: 6, sm: 3 },
    rules: [
      { rule: "isCdi", override: { hidden: true } },
      { rule: "isOfferLocked", override: { disabled: true } },
    ],
  },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "Unité",
    scope: "contractDurationUnit",
    columns: { xs: 6, sm: 3 },
    rules: [
      { rule: "isCdi", override: { hidden: true } },
      { rule: "isOfferLocked", override: { disabled: true } },
    ],
  },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "Offre permanente",
    scope: "isPermanent",
    columns: { xs: 12 },
    rules: [{ rule: "isOfferLocked", override: { disabled: true } }],
  },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "Date de début",
    scope: "startDate",
    columns: { xs: 12, sm: 6 },
    rules: [{ rule: "isOfferLocked", override: { disabled: true } }],
  },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "Date d'expiration *",
    scope: "expirationDate",
    columns: { xs: 12, sm: 6 },
    rules: [
      { rule: "isPermanentOffer", override: { disabled: true } },
      { rule: "isOfferLocked", override: { disabled: true } },
    ],
  },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "Durée hebdo (en heures)",
    scope: "workWeeklyDuration",
    columns: { xs: 12, sm: 6 },
    rules: [{ rule: "isOfferLocked", override: { disabled: true } }],
  },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "Qui a trouvé l'offre *",
    scope: "externalSource",
    renderAs: "radio",
    columns: { xs: 12 },
  },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "",
    scope: "description",
    columns: { xs: 12 },
    rules: [{ rule: "isOfferLocked", override: { disabled: true } }],
  },
  {
    type: EnumFormGenComponentType.TITLE,
    title: "Entreprise",
    columns: { xs: 12 },
  },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "Entreprise *",
    scope: "enterprise",
    columns: { xs: 12 },
    rules: [{ rule: "isOfferLocked", override: { disabled: true } }],
  },
  {
    type: EnumFormGenComponentType.INPUT,
    label: "Contact",
    scope: "contact",
    columns: { xs: 12 },
    rules: [{ rule: "isOfferLocked", override: { disabled: true } }],
  },
];

export const offerFormSchema: IFormGenSchema<typeof offerJsonSchema> = {
  version: "1.0",
  jsonSchema: offerJsonSchema,
  uiSchema: offerUiSchema,
};
